import React, { useEffect, useRef, useState } from 'react';

/**
 * The signed-in user, and the way out.
 *
 * Sign-out used to live in two places at once — a dropdown in the top bar and
 * a pill at the foot of the sidebar. The top bar is gone and the sidebar foot
 * carries The May Firm mark, so this is now the only one, and it has to be
 * reachable from every tab rather than only the dashboard.
 *
 * The photo is whatever is on the user's Google Workspace account: read and
 * stored on every sign-in, so nobody uploads anything and it stays current by
 * itself. Only a real photo is shown — see isGeneratedAvatar.
 */

/**
 * Is this Google's stand-in rather than somebody's actual photo?
 *
 * When an account has no photo, Google still returns a picture URL: a single
 * letter on one of its own brand colours, or the grey silhouette. They are
 * real image files and load perfectly well, which is the problem — a purple
 * square with one initial is another company's design dropped into the middle
 * of this one, and it shows one initial where our own block shows two.
 *
 * Caught by URL, which is reliable for the shapes Google actually serves:
 *   .../a/default-user=s96-c          the grey silhouette
 *   .../a-/default-user               older form of the same
 *   .../-/AOh14G.../mo/...            the generated monogram
 * A real uploaded photo carries a content id instead. If Google ever invents
 * a fourth form the fallback is simply the old behaviour — their image shows
 * — rather than a broken avatar, which is the right way round for a guess.
 */
function isGeneratedAvatar(url) {
  if (typeof url !== 'string' || !url) return true;
  return /\/a[-/]?\/?default-user/i.test(url) || /\/mo\//i.test(url);
}
/**
 * Second line of defence: look at the image rather than its address.
 *
 * The URL test only knows the placeholder shapes Google serves today, and
 * the one account whose avatar anyone notices first is their own. A
 * monogram is a flat background with one letter on it, so it resolves to a
 * handful of colours; a photograph resolves to dozens. Sample a small grid
 * and count.
 *
 * Every failure path shows the image: a tainted canvas when Google omits
 * CORS headers, a browser without canvas, anything unexpected. A guess about
 * somebody's face should fall back to showing it, never to hiding it.
 */
function looksGenerated(img) {
  try {
    const N = 10;
    const c = document.createElement('canvas');
    c.width = N; c.height = N;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return false;
    ctx.drawImage(img, 0, 0, N, N);
    const { data } = ctx.getImageData(0, 0, N, N);
    const seen = new Set();
    for (let i = 0; i < data.length; i += 4) {
      // Quantised to 32 levels a channel: a photo still spreads across many
      // buckets, while antialiasing on a letterform does not invent new ones.
      seen.add(`${data[i] >> 5},${data[i + 1] >> 5},${data[i + 2] >> 5}`);
      if (seen.size > 6) return false;
    }
    return true;
  } catch (e) {
    return false;
  }
}

function ProfileMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const [imgGenerated, setImgGenerated] = useState(false);
  const wrap = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  if (!user) return null;

  const name = user.name || user.email || 'Signed in';
  const initials = (() => {
    const parts = String(user.name || user.email || '').split(/[\s@.]+/).filter(Boolean);
    return parts.slice(0, 2).map((p) => (p[0] || '').toUpperCase()).join('') || 'U';
  })();

  return (
    <div className="lm-profile-wrap" ref={wrap}>
      <button type="button" className="lm-user" onClick={() => setOpen((o) => !o)}
              aria-haspopup="menu" aria-expanded={open}>
        <span className="lm-avatar">
          {user.picture && !isGeneratedAvatar(user.picture)
            && !imgFailed && !imgGenerated ? (
            <img src={user.picture} alt="" crossOrigin="anonymous"
                 onError={() => setImgFailed(true)}
                 onLoad={(e) => { if (looksGenerated(e.currentTarget)) setImgGenerated(true); }} />
          ) : initials}
        </span>
        <span className="lm-nm">{name}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>
      {open && (
        <div className="lm-menu" role="menu">
          <div className="lm-who">
            <b>{user.name || 'Signed in'}</b>
            <span>{user.email || ''}</span>
          </div>
          <button type="button" role="menuitem"
                  onClick={() => { setOpen(false); if (onLogout) onLogout(); }}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default ProfileMenu;
